import { Mobile, notifyError, panelType, ProjectHelper } from '@bimser/common';
import { BCMessage, BCNotification, findMessage } from '@bimser/components';
import _isHead from 'lodash/head';
import { setPendingFastApproveDocumentCount as dashboardSetPendingFastApproveDocumentCount } from 'modules/dashboard/actions';
import { call, put } from 'redux-saga/effects';
import { getFlowDocumentNameFromDocument } from '../../../../../../app-main/layouts/left-menu/sagas/leftMenuManagerTransaction';
import {
	actionRole,
	IRelatedPanel,
	WebInterfaceActionFactory
} from '../../../../../../common/entities';
import { openNewPanelInsteadOfTheOtherOne } from '../../../../../../common/sagas/commonSagas';
import { IFlowViewerProps } from '../../entities';
import { IFlowStartParametersResponse } from '../../entities/IFlowStartParametersResponse';
import { IFlowDocument } from '../../entities/models/IFlowDocument';
import { FlowDocumentType } from '../../entities/enums';
import { setPendingFastApproveDocumentCount as workflowSetPendingFastApproveDocumentCount } from 'modules/workflow/actions/setPendingFastApproveDocumentCount';

export interface FlowViewerParameters {
	parent?: IRelatedPanel;
	processId?: number;
	requestId?: number;
	projectName: string;
	flowName: string;
	projectVersion?: number;
	projectCaption?: string;
	panelSize?: 1 | 2 | 3;
	closePanelGuid?: string;
	scopeId?: string;
	processRequestSecretKey?: string;
	mobileFormView?: Mobile.MobileFormView;
	forMobile?: boolean;
	fastEventId?: number;
	then?: () => void;
	catch?: (error: any) => void;
	hideHistoryAfterEvent: boolean;
	originalProjectVersion?: number;
}

export function* OpenFlowViewer(parameters: FlowViewerParameters) {
	try {
		if (!parameters.fastEventId) {
			BCMessage.loading({
				key: 'openFlowViewerLoading',
				content: findMessage.get('100127'),
				duration: 0
			});
		}

		const request: any = {
			processId: parameters.processId || 0,
			requestId: parameters.requestId || 0
		};

		parameters.projectVersion = parameters.projectVersion || 0;

		if ((request.requestId <= 0 && request.processId <= 0) || parameters.projectVersion === 0) {
			delete parameters.projectVersion;
		}
		if (request.processId > 0) {
			request.requestId = parameters.requestId;
		}
		if (parameters.scopeId) {
			request.draftScopeId = parameters.scopeId;
		}

		const response = yield call(ProjectHelper.RunGeneratorFlowService, {
			projectName: parameters.projectName,
			projectVersion: parameters.projectVersion,
			flowName: parameters.flowName,
			serviceName: 'StartParameters',
			requestParameters: request,
			serviceMethod: 'post'
		});
		const flowStartParameters: IFlowStartParametersResponse = response.data;
		const document = _isHead(flowStartParameters.documents);

		if (parameters.fastEventId) {
			// Açılması beklenecek olan form sayısını set et
			const formDocumentCount = flowStartParameters.documents.filter(
				d => d.type === FlowDocumentType.CreateForm
			).length;

			if (parameters.parent?.panelType === panelType.wfProcessList) {
				yield put(
					workflowSetPendingFastApproveDocumentCount(
						WebInterfaceActionFactory({
							data: formDocumentCount
						})
					)
				);
			} else {
				yield put(dashboardSetPendingFastApproveDocumentCount(formDocumentCount));
			}
		}

		return yield openDocument(document, parameters, flowStartParameters);
	} catch (error) {
		if (error?.message == '-1') {
			BCNotification.warning({ message: findMessage.get('100742') });
		} else {
			yield call(notifyError, error);
		}
		return null;
	} finally {
		BCMessage.destroy('openFlowViewerLoading');
	}
}

function* openDocument(
	document: IFlowDocument,
	parameters: FlowViewerParameters,
	flowStartParameters: IFlowStartParametersResponse
) {
	const clientUrl = ProjectHelper.getDeploymentUrl(
		parameters.projectName,
		null,
		parameters.projectVersion
	);

	let defDocumentName = null;
	let title = parameters.projectCaption;
	let panelSize = 2;
	if (document) {
		defDocumentName = getFlowDocumentNameFromDocument(document);
		title = document.viewOptions.formCaption;
		panelSize = document.viewOptions.panelSize || 2;

		if (
			document.viewOptions.projectName &&
			document.viewOptions.projectName !== parameters.projectName
		) {
			// External document is going to open with latest version
			parameters.originalProjectVersion = parameters.projectVersion;
			parameters.projectVersion = null;
		}
	}

	const startProps: IFlowViewerProps = {
		...parameters,
		clientUrl,
		flowStartParameters,
		activeDocumentName: defDocumentName,
		mobileFormView: parameters.mobileFormView || Mobile.MobileFormView.WebView,
		fromMobile: parameters.forMobile,
		hideHistoryAfterEvent: parameters.hideHistoryAfterEvent
	};

	return yield call(openNewPanelInsteadOfTheOtherOne, parameters.closePanelGuid, {
		title: title,
		paneltype: panelType.flowViewer,
		parent: parameters.parent,
		role: actionRole.flowForm,
		initialSize: parameters.panelSize || panelSize,
		size: parameters.panelSize || panelSize,
		extraProps: startProps,
		canHandleCloseEvent: false,
		fastEventId: parameters.fastEventId,
		forMobile: parameters.forMobile
	});
}
